
import { DOCUMENT, Injectable, inject } from '@angular/core';
import { PlatformService } from '../platform/platform.service';


export enum FacebookAction {
  Login = 'login',
  Register = 'CompleteRegistration',
  AddToCart = 'AddToCart',
  Purchase = 'Purchase',
  Checkout = 'InitiateCheckout',
  PaymentSuccess = 'PaymentSuccess',
  PaymentFailure = 'PaymentFailure',
  PropertyId = 'propertyId',
  // to be done
  ViewContent = 'ViewContent',
  BidInitiated = 'BidInitiated',
  AIProductDetails = 'AIProductDetails',
  AddPaymentInfo = 'AddPaymentInfo',
  Search = 'Search'
}

const fbq_keyword = 'fbq'

@Injectable({
  providedIn: 'root'
})
export class FacebookAnalyticsService {
  private document = inject<Document>(DOCUMENT);
  private platformService = inject(PlatformService);
  private hasWarnedAboutFbq = false; // Track if we've already warned about missing fbq
  private fbqCheckInterval: any = null; // Track interval for checking fbq availability

  private checkFbqAvailability(): boolean {
    return typeof (window as any).fbq === 'function';
  }

  private waitForFbqAndRetry(callback: () => void, maxRetries = 10): void {
    if (this.checkFbqAvailability()) {
      callback();
      return;
    }

    if (maxRetries <= 0) {
      if (!this.hasWarnedAboutFbq) {
        this.hasWarnedAboutFbq = true;
      }
      return;
    }

    // Clear any existing interval
    if (this.fbqCheckInterval) {
      clearTimeout(this.fbqCheckInterval);
    }

    // Retry after a short delay
    this.fbqCheckInterval = setTimeout(() => {
      this.waitForFbqAndRetry(callback, maxRetries - 1);
    }, 1000); // Wait 1 second before retrying
  }

  private prepareScript({
    action,
    value = 0,
    contents = [],
    content_name = ''
  }: {
    action: any;
    value?: number;
    contents?: any[];
    content_name?: string;
  }) {

    let script = `fbq('track', '${action}', {
      value: ${value},
      currency: 'INR'
    });`;

    if (action === FacebookAction.Search) {
      script = `fbq('track', '${action}', {
        value: ${value},
        currency: 'INR',
        search_string: '${content_name}'
      });`;
      return script;
    }

    if (Array.isArray(contents) && contents?.length) {
      script = `fbq('track', '${action}', {
       value: ${value},
       currency: 'INR',
       contents: ${JSON.stringify(contents)},
       content_type: 'product'
       });`;
    }

    if (content_name) {
      script = `fbq('track', '${action}', {
        value: ${value},
        currency: 'INR',
        content_name: '${content_name}',
        content_type: 'product'
      });`;
    }


    return script;
  }

  private prepareCustomScript({
    action,
    value = 0
  }: {
    action: any;
    value?: number;
  }) {

    const script = `fbq('trackCustom', '${action}', {
      value: ${value},
      currency: 'INR'
    });`

    return script;
  }


  injectScript({
    action,
    value = 0,
    contents = [],
    content_name = '',
    custom_event = false
  }: {
    action: any;
    value?: number;
    contents?: any[];
    content_name?: string;
    custom_event?: boolean;
  }) {

    const scriptId = action + fbq_keyword;

    if (!this.platformService.isBrowser()) {
      return;
    }

    // Use reactive approach to wait for fbq and retry
    this.waitForFbqAndRetry(() => {
      let scriptElement = this.document.getElementById(scriptId) as HTMLScriptElement;
      if (scriptElement) {
        this.removeScript(scriptId);
      }

      scriptElement = this.document.createElement('script');
      scriptElement.id = scriptId;

      try {
        scriptElement.innerHTML = custom_event ? this.prepareCustomScript({ action, value }) : this.prepareScript({ action, value, contents, content_name });
        this.document.head.appendChild(scriptElement);
      } catch (error) {
      }
    });
  }




  removeScript(scriptId: string) {
    const scriptElement = this.document.getElementById(scriptId) as HTMLScriptElement;
    if (scriptElement) {
      scriptElement.remove();
    }
  }

}
